import CreateColumn from '../../../../Services/agGrid/createColumn';
import CustomHeader from '../../../../Components/AgGrid/CustomHeader';
import MultiChipSelect from '../../../../Components/MUIs/MultiChipSelect';

/**
 * PPE_BASIC_01(보호구 등록) 컬럼 정의
 * 서버 응답: PascalCase (ErCd, EquipNm, Spec, Price, Unit, Remark, PaymentStandard, UseDepartmentScope, ManagerDepartments, ManagerId)
 */
const ALL_DEPARTMENT_VALUE = '__ALL__';
const MANAGER_DEPT_MAX_CHIP_LINES = 3;
const MANAGER_DEPT_CHIPS_PER_ROW = 3;
const MANAGER_DEPT_CHIP_LINE_HEIGHT = 28;
const MANAGER_DEPT_BASE_ROW_HEIGHT = 42;

/** 사용부서 chip 줄 수에 따른 ag-Grid 행 높이 */
export const getManagerDepartmentRowHeight = (params) => {
  if (!params?.data) return MANAGER_DEPT_BASE_ROW_HEIGHT;
  if (params.data.useDepartmentScope === 'ALL') return MANAGER_DEPT_BASE_ROW_HEIGHT;

  const count = toDepartmentArray(params.data.managerDepartments).length;
  if (count <= 0) return MANAGER_DEPT_BASE_ROW_HEIGHT;

  const lines = Math.min(
    MANAGER_DEPT_MAX_CHIP_LINES,
    Math.ceil(count / MANAGER_DEPT_CHIPS_PER_ROW)
  );
  return (
    MANAGER_DEPT_BASE_ROW_HEIGHT +
    Math.max(0, lines - 1) * MANAGER_DEPT_CHIP_LINE_HEIGHT
  );
};

const toDepartmentArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
};

const getDepartmentName = (depList, value) => {
  const match = (depList || []).find((d) => d.value === value);
  return match ? match.name : value;
};

const DepartmentChipCell = (params) => {
  const depList =
    params.context?.depList || params.colDef?.cellRendererParams?.depList || [];
  const currentScope = params.data?.useDepartmentScope || 'SELECTED';
  const currentDepartments = toDepartmentArray(
    params.data?.managerDepartments || params.data?.managerDepartment
  );
  const selected =
    currentScope === 'ALL' ? [ALL_DEPARTMENT_VALUE] : currentDepartments;

  const items = [
    { value: ALL_DEPARTMENT_VALUE, label: '전체' },
    ...depList.map((d) => ({ value: d.value, label: d.name })),
  ];

  const handleChange = (event) => {
    const rawValue = event.target.value || [];
    const nextValue =
      rawValue[rawValue.length - 1] === ALL_DEPARTMENT_VALUE
        ? [ALL_DEPARTMENT_VALUE]
        : rawValue.filter((v) => v !== ALL_DEPARTMENT_VALUE);

    if (nextValue.includes(ALL_DEPARTMENT_VALUE)) {
      params.data.useDepartmentScope = 'ALL';
      params.data.managerDepartments = [];
      params.data.managerDepartmentNames = '전체';
      params.node.setDataValue('managerDepartment', '');
      markRowAsChanged(params);
      params.api?.resetRowHeights?.();
      return;
    }

    const names = nextValue.map((value) => getDepartmentName(depList, value));
    params.data.useDepartmentScope = 'SELECTED';
    params.data.managerDepartments = nextValue;
    params.data.managerDepartmentNames = names.join(', ');
    params.node.setDataValue('managerDepartment', nextValue[0] || '');
    markRowAsChanged(params);
    params.api?.resetRowHeights?.();
  };

  return (
    <MultiChipSelect
      items={items}
      value={selected}
      onChange={handleChange}
      labelYn={false}
      maxChipLines={MANAGER_DEPT_MAX_CHIP_LINES}
      estimatedChipsPerRow={MANAGER_DEPT_CHIPS_PER_ROW}
      MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
      sx={{
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '& .MuiSelect-select': {
          alignItems: 'flex-start',
          display: 'flex',
          minHeight: 30,
          padding: '2px 4px',
          whiteSpace: 'normal',
        },
      }}
    />
  );
};

const markRowAsChanged = (params) => {
  const setChangedRowData = params.context?.setChangedRowData;
  if (typeof setChangedRowData !== 'function') return;

  const rowNo = params.data?.no;
  if (!rowNo) return;

  const nextRow = { ...params.data, editYn: 'Y' };
  params.node?.setData?.(nextRow);

  setChangedRowData((current) => {
    const safe = Array.isArray(current) ? current : [];
    const idx = safe.findIndex((c) => c.no === rowNo);
    if (idx > -1) {
      const next = [...safe];
      next[idx] = nextRow;
      return next;
    }
    return [...safe, nextRow];
  });
};

export const getColumnDefs1 = (props) => {
  return [
    CreateColumn('no', 'NO', 'center', 80, false, false, false, 'checkbox', {
      maxWidth: 80,
      checkboxProps: {
        DeleteNewRow1: props.DeleteNewRow1,
        RestoreRow1: props.RestoreRow1,
        gridRef1: props.gridRef1,
        rowData1: props.rowData1,
        selectedRowNosRef: props.selectedRowNosRef,
        lastCheckedIndexRef: props.lastCheckedIndexRef,
        handleRowSelection: props.handleRowSelection,
      },
    }),
    CreateColumn(
      'equipNm',
      '보호구명',
      'left',
      220,
      true,
      true,
      false,
      'text',
      {
        minWidth: 140,
        maxLength: 100,
        headerComponent: CustomHeader,
        headerComponentParams: { required: true },
        editableCondition: (params) => params?.data?.newYn === 'Y',
        tooltipValueGetter: (params) => {
          if (params?.data?.newYn !== 'Y') {
            return '저장된 데이터는 수정할 수 없습니다.';
          }
          return null;
        },
        customCellStyle: (params) => {
          const base = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          };
          if (params?.data?.newYn !== 'Y') {
            return {
              ...base,
              backgroundColor: '#f5f5f5',
              color: '#666',
              fontStyle: 'italic',
              cursor: 'not-allowed',
            };
          }
          return base;
        },
      }
    ),
    CreateColumn('spec', '규격', 'left', 200, true, false, false, 'text', {
      minWidth: 140,
      maxLength: 100,
    }),
    CreateColumn('price', '단가', 'right', 120, true, false, false, 'number', {
      minWidth: 100,
      valueFormatter: (params) => {
        const v = params?.value;
        if (v == null || v === '') return '';
        const n = typeof v === 'number' ? v : Number(v);
        return Number.isFinite(n) ? n.toLocaleString('ko-KR') : String(v);
      },
      valueParser: (params) => {
        if (params.newValue == null || params.newValue === '') return null;
        const raw = String(params.newValue).replace(/[^0-9]/g, '');
        if (!raw) return null;
        const parsed = parseInt(raw, 10);
        return Number.isFinite(parsed) ? Math.min(parsed, 2147483647) : null;
      },
    }),
    CreateColumn('unit', '단위', 'center', 100, true, false, false, 'select', {
      minWidth: 90,
      selectOptions: ['EA', 'SET', '기타'],
    }),
    CreateColumn(
      'paymentStandard',
      '교체주기',
      'left',
      180,
      true,
      false,
      false,
      'text',
      {
        minWidth: 160,
        maxLength: 100,
      }
    ),
    CreateColumn('stockQty', '재고', 'right', 180, true, false, false, 'number', {
      minWidth: 160,
      valueFormatter: (params) => {
        const v = params?.value;
        if (v == null || v === '') return '';
        const n = typeof v === 'number' ? v : Number(v);
        if (!Number.isFinite(n)) return '';
        const clamped = Math.max(0, Math.trunc(n));
        return String(clamped);
      },
      valueParser: (params) => {
        if (
          params.newValue === null ||
          params.newValue === undefined ||
          params.newValue === ''
        ) {
          return null;
        }
        const raw = String(params.newValue).replace(/[^0-9]/g, '');
        if (!raw) return null;
        const parsed = parseInt(raw, 10);
        if (!Number.isFinite(parsed)) return null;
        return Math.min(Math.max(0, parsed), 2147483647);
      },
    }),
    CreateColumn(
      'filePath',
      '첨부',
      'center',
      200,
      false,
      false,
      false,
      'fileButton',
      {
        minWidth: 160,
        valueGetter: (params) => {
          const d = params.data;
          if (!d) return '';
          return d.fileName || '';
        },
        fileButtonClickHandler: (data) => {
          props.PopupFileClickEvent?.(data);
        },
      }
    ),
    CreateColumn(
      'managerDepartment',
      '사용부서',
      'left',
      300,
      false,
      false,
      false,
      'text',
      {
        minWidth: 260,
        cellRenderer: DepartmentChipCell,
        cellRendererParams: {
          depList: props.depList || [],
        },
        tooltipValueGetter: (params) => {
          if (params.data?.useDepartmentScope === 'ALL') return '전체';
          return params.data?.managerDepartmentNames || '';
        },
        valueFormatter: (params) => {
          if (params.data?.useDepartmentScope === 'ALL') return '전체';
          return params.data?.managerDepartmentNames || '';
        },
        customCellStyle: {
          display: 'flex',
          alignItems: 'flex-start',
          overflow: 'hidden',
        },
      }
    ),
    CreateColumn(
      'managerId',
      '담당자',
      'left',
      140,
      true,
      false,
      false,
      'text',
      {
        minWidth: 120,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: (props.memberList || []).map((m) => m.value),
        },
        valueFormatter: (params) => {
          if (!params.value) return '';
          const match = (props.memberList || []).find(
            (m) => m.value === params.value
          );
          return match ? match.name : params.value;
        },
        valueParser: (params) => {
          // 입력된 값이 이름인 경우 코드값으로 변환, 이미 코드값이면 그대로 반환
          const inputValue = params.newValue || '';
          if (!inputValue) return '';

          // 코드값인지 확인
          const codeMatch = (props.memberList || []).find(
            (m) => m.value === inputValue
          );
          if (codeMatch) return codeMatch.value;

          // 이름인 경우 코드값으로 변환
          const nameMatch = (props.memberList || []).find(
            (m) => m.name === inputValue || m.name === String(inputValue).trim()
          );
          return nameMatch ? nameMatch.value : inputValue;
        },
      }
    ),
    CreateColumn('remark', '비고', 'left', 200, true, false, false, 'text', {
      flex: 1,
      minWidth: 140,
      maxLength: 100,
    }),
  ];
};

const columnDefs = { getColumnDefs1 };
export default columnDefs;
